import * as THREE from "three";

const A2F_TEXTURE_UPDATER_MODELS = ["be0dcf0f-c72b-48f7-a462-6ba7560498d7alan"]; //These script is needed if you want to change the texture of the model manually.

export class TextureUpdater {
    constructor() {
        this.panel = null;
        this.meshList = [];
        this.selectedMesh = null;
        this.selectedMaterialIndex = 0;
        this.originalTextures = new Map();
        this.isVisible = false;
        this.modelViewer = null;
        this._boundOnModelLoad = this._onModelLoadComplete.bind(this);
        this._initialized = false;
    }

    init() {
        if (this._initialized) return;
        this._initialized = true;

        this.panel = document.getElementById("texture-updater-panel");
        if (!this.panel) {
            console.warn("[TextureUpdater] Panel element not found");
            return;
        }

        this._setupEventListeners();
        document.addEventListener(
            "modelLoadingComplete",
            this._boundOnModelLoad,
        );
        document.addEventListener("character-changed", () => {
            setTimeout(() => this._onModelLoadComplete(), 200);
        });

        console.log("[TextureUpdater] Initialized");

        setTimeout(() => this._onModelLoadComplete(), 500);
    }

    _getMaterial(mesh, index) {
        if (!mesh || !mesh.material) return null;
        if (Array.isArray(mesh.material)) {
            const idx =
                index !== undefined ? index : this.selectedMaterialIndex;
            return mesh.material[idx] || null;
        }
        return mesh.material;
    }

    _getMaterialCount(mesh) {
        if (!mesh || !mesh.material) return 0;
        return Array.isArray(mesh.material) ? mesh.material.length : 1;
    }

    _expandFlattenedMaterials(mesh) {
        if (!mesh || !mesh.material || !mesh.geometry) return false;
        if (Array.isArray(mesh.material)) return false;

        const groups = mesh.geometry.groups;
        if (!groups || groups.length < 2) return false;

        const uniqueIndices = new Set(groups.map((g) => g.materialIndex));
        if (uniqueIndices.size < 2) return false;

        const maxIndex = Math.max(...uniqueIndices);
        const matArray = [];
        for (let i = 0; i <= maxIndex; i++) {
            matArray.push(mesh.material.clone());
        }
        mesh.material = matArray;
        console.log(
            `[TextureUpdater] Expanded "${mesh.name}" from 1 material to ${matArray.length} (based on ${groups.length} geometry groups)`,
        );
        return true;
    }

    _onModelLoadComplete() {
        const viewer = window.modelViewer;
        if (!viewer) return;

        this.modelViewer = viewer;
        const currentId =
            viewer.currentModelId ||
            viewer.currentModelKey ||
            (viewer.modelManager && viewer.modelManager.currentModelId);

        if (A2F_TEXTURE_UPDATER_MODELS.includes(currentId)) {
            this._populateMeshList();
            this._showToggleButton();
        } else {
            this._hidePanel();
            this._hideToggleButton();
        }
    }

    _setupEventListeners() {
        const toggleBtn = document.getElementById("texture-updater-toggle");
        if (toggleBtn) {
            toggleBtn.addEventListener("click", () => this._togglePanel());
        }

        const closeBtn = document.getElementById("texture-updater-close");
        if (closeBtn) {
            closeBtn.addEventListener("click", () => this._hidePanel());
        }

        const meshSelect = document.getElementById(
            "texture-updater-mesh-select",
        );
        if (meshSelect) {
            meshSelect.addEventListener("change", (e) =>
                this._onMeshSelected(e.target.value),
            );
        }

        const materialSelect = document.getElementById(
            "texture-updater-material-select",
        );
        if (materialSelect) {
            materialSelect.addEventListener("change", (e) =>
                this._onMaterialSelected(parseInt(e.target.value, 10)),
            );
        }

        const fileInput = document.getElementById("texture-updater-file");
        if (fileInput) {
            fileInput.addEventListener("change", (e) =>
                this._onFileSelected(e),
            );
        }

        const applyBtn = document.getElementById("texture-updater-apply");
        if (applyBtn) {
            applyBtn.addEventListener("click", () => this._applyTexture());
        }

        const resetBtn = document.getElementById("texture-updater-reset");
        if (resetBtn) {
            resetBtn.addEventListener("click", () => this._resetTexture());
        }

        const resetAllBtn = document.getElementById(
            "texture-updater-reset-all",
        );
        if (resetAllBtn) {
            resetAllBtn.addEventListener("click", () =>
                this._resetAllTextures(),
            );
        }

        const mapTypeSelect = document.getElementById(
            "texture-updater-map-type",
        );
        if (mapTypeSelect) {
            mapTypeSelect.addEventListener("change", () =>
                this._updatePreview(),
            );
        }
    }

    _showToggleButton() {
        const btn = document.getElementById("texture-updater-toggle");
        if (btn) btn.style.display = "flex";
    }

    _hideToggleButton() {
        const btn = document.getElementById("texture-updater-toggle");
        if (btn) btn.style.display = "none";
    }

    _togglePanel() {
        if (this.isVisible) {
            this._hidePanel();
        } else {
            this._showPanel();
        }
    }

    _showPanel() {
        if (!this.panel) return;
        this.panel.classList.add("texture-updater-visible");
        this.isVisible = true;
        this._populateMeshList();
    }

    _hidePanel() {
        if (!this.panel) return;
        this.panel.classList.remove("texture-updater-visible");
        this.isVisible = false;
    }

    _populateMeshList() {
        const viewer = this.modelViewer || window.modelViewer;
        if (!viewer || !viewer.model) return;

        const meshSelect = document.getElementById(
            "texture-updater-mesh-select",
        );
        if (!meshSelect) return;

        this.meshList = [];
        meshSelect.innerHTML = '<option value="">-- Select a mesh --</option>';

        viewer.model.traverse((child) => {
            if (child.isMesh) {
                this._expandFlattenedMaterials(child);
                this.meshList.push(child);
                const option = document.createElement("option");
                option.value = child.uuid;
                const matCount = this._getMaterialCount(child);
                const mat = this._getMaterial(child, 0);
                const matType = mat ? mat.type : "none";
                const hasMap = mat && mat.map ? " [has texture]" : "";
                const matLabel =
                    matCount > 1 ? ` (${matCount} materials)` : ` (${matType})`;
                option.textContent = `${child.name || "Unnamed"}${hasMap}${matLabel}`;
                meshSelect.appendChild(option);
            }
        });

        console.log(`[TextureUpdater] Found ${this.meshList.length} meshes`);
    }

    _onMeshSelected(uuid) {
        this.selectedMesh = this.meshList.find((m) => m.uuid === uuid) || null;
        this.selectedMaterialIndex = 0;

        if (this.selectedMesh) {
            const m = this.selectedMesh;
            console.log("[TextureUpdater] Mesh selected:", m.name);
            console.log(
                "[TextureUpdater] isArray?",
                Array.isArray(m.material),
                "matCount:",
                Array.isArray(m.material) ? m.material.length : 1,
            );
            console.log(
                "[TextureUpdater] groups:",
                m.geometry?.groups?.length,
                m.geometry?.groups?.slice(0, 5),
            );
        }

        this._updateMaterialSelector();
        this._updatePreview();
        this._updateMeshInfo();
    }

    _onMaterialSelected(index) {
        this.selectedMaterialIndex = index;
        this._updatePreview();
        this._updateMeshInfo();
    }

    _updateMaterialSelector() {
        const field = document.getElementById("texture-updater-material-field");
        const select = document.getElementById(
            "texture-updater-material-select",
        );
        if (!field || !select) return;

        const matCount = this._getMaterialCount(this.selectedMesh);

        if (matCount > 1) {
            select.innerHTML = "";
            for (let i = 0; i < matCount; i++) {
                const mat = this._getMaterial(this.selectedMesh, i);
                const option = document.createElement("option");
                option.value = i;
                const matName = mat && mat.name ? mat.name : `Material ${i}`;
                const matType = mat ? mat.type : "unknown";
                const hasMap = mat && mat.map ? " [has texture]" : "";
                option.textContent = `${matName}${hasMap} (${matType})`;
                select.appendChild(option);
            }
            select.value = "0";
            field.style.display = "";
        } else {
            field.style.display = "none";
        }
    }

    _updateMeshInfo() {
        const infoEl = document.getElementById("texture-updater-mesh-info");
        if (!infoEl) return;

        if (!this.selectedMesh) {
            infoEl.innerHTML = "";
            return;
        }

        const mesh = this.selectedMesh;
        const mat = this._getMaterial(mesh);
        const matCount = this._getMaterialCount(mesh);

        let info = `<strong>Mesh:</strong> ${mesh.name || "Unnamed"}<br>`;
        info += `<strong>Materials:</strong> ${matCount}<br>`;

        if (matCount > 1) {
            info += `<strong>Selected:</strong> ${this.selectedMaterialIndex}<br>`;
        }

        if (mat) {
            info += `<strong>Type:</strong> ${mat.type}<br>`;
            info += `<strong>Color:</strong> #${mat.color ? mat.color.getHexString() : "N/A"}<br>`;
            info += `<strong>Map:</strong> ${mat.map ? "Yes" : "No"}<br>`;
            info += `<strong>Normal Map:</strong> ${mat.normalMap ? "Yes" : "No"}<br>`;
            info += `<strong>Roughness Map:</strong> ${mat.roughnessMap ? "Yes" : "No"}<br>`;
            info += `<strong>Metalness Map:</strong> ${mat.metalnessMap ? "Yes" : "No"}<br>`;
        }

        infoEl.innerHTML = info;
    }

    _updatePreview() {
        const previewEl = document.getElementById("texture-updater-preview");
        if (!previewEl) return;

        const mat = this._getMaterial(this.selectedMesh);
        if (!this.selectedMesh || !mat) {
            previewEl.innerHTML =
                '<div class="texture-updater-no-preview">No mesh selected</div>';
            return;
        }

        const mapType =
            document.getElementById("texture-updater-map-type")?.value || "map";
        const texture = mat[mapType];

        if (texture && texture.image) {
            const canvas = document.createElement("canvas");
            canvas.width = 128;
            canvas.height = 128;
            const ctx = canvas.getContext("2d");
            try {
                ctx.drawImage(texture.image, 0, 0, 128, 128);
                previewEl.innerHTML = "";
                const img = document.createElement("img");
                img.src = canvas.toDataURL();
                img.alt = `Current ${mapType}`;
                img.className = "texture-updater-preview-img";
                previewEl.appendChild(img);
            } catch (e) {
                previewEl.innerHTML =
                    '<div class="texture-updater-no-preview">Cannot preview (CORS)</div>';
            }
        } else {
            previewEl.innerHTML = `<div class="texture-updater-no-preview">No ${mapType} on this material</div>`;
        }
    }

    _onFileSelected(event) {
        const file = event.target.files[0];
        if (!file) return;

        const newPreview = document.getElementById(
            "texture-updater-new-preview",
        );
        if (newPreview) {
            const reader = new FileReader();
            reader.onload = (e) => {
                newPreview.innerHTML = "";
                const img = document.createElement("img");
                img.src = e.target.result;
                img.alt = "New texture";
                img.className = "texture-updater-preview-img";
                newPreview.appendChild(img);
            };
            reader.readAsDataURL(file);
        }
    }

    _applyTexture() {
        if (!this.selectedMesh) {
            this._showStatus("Please select a mesh first", "error");
            return;
        }

        const fileInput = document.getElementById("texture-updater-file");
        if (!fileInput || !fileInput.files[0]) {
            this._showStatus("Please select a texture file first", "error");
            return;
        }

        const mapType =
            document.getElementById("texture-updater-map-type")?.value || "map";
        const file = fileInput.files[0];
        const mesh = this.selectedMesh;

        const matIndex = Array.isArray(mesh.material)
            ? this.selectedMaterialIndex
            : 0;
        const mat = this._getMaterial(mesh, matIndex);

        if (!mat) {
            this._showStatus("Selected material not found", "error");
            return;
        }

        const key = `${mesh.uuid}::${matIndex}::${mapType}`;
        if (!this.originalTextures.has(key)) {
            this.originalTextures.set(key, mat[mapType] || null);
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const texture = new THREE.Texture(img);
                texture.needsUpdate = true;
                texture.colorSpace =
                    mapType === "map"
                        ? THREE.SRGBColorSpace
                        : THREE.LinearSRGBColorSpace;
                texture.flipY = true;
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;

                mat[mapType] = texture;
                mat.needsUpdate = true;

                this._updatePreview();
                const matLabel = Array.isArray(mesh.material)
                    ? ` [mat ${matIndex}]`
                    : "";
                this._showStatus(
                    `${mapType} applied to "${mesh.name}"${matLabel}`,
                    "success",
                );
                console.log(
                    `[TextureUpdater] Applied ${mapType} to "${mesh.name}" material ${matIndex}`,
                );
            };
            img.src = e.target.result;
        };

        reader.readAsDataURL(file);
    }

    _resetTexture() {
        if (!this.selectedMesh) {
            this._showStatus("Please select a mesh first", "error");
            return;
        }

        const mapType =
            document.getElementById("texture-updater-map-type")?.value || "map";
        const key = `${this.selectedMesh.uuid}::${this.selectedMaterialIndex}::${mapType}`;

        const mat = this._getMaterial(this.selectedMesh);
        if (this.originalTextures.has(key) && mat) {
            mat[mapType] = this.originalTextures.get(key);
            mat.needsUpdate = true;
            this.originalTextures.delete(key);
            this._updatePreview();
            this._showStatus(
                `${mapType} reset on "${this.selectedMesh.name}"`,
                "success",
            );
        } else {
            this._showStatus("No original texture to restore", "error");
        }
    }

    _resetAllTextures() {
        this.originalTextures.forEach((originalTex, key) => {
            const [uuid, matIndexStr, mapType] = key.split("::");
            const matIndex = parseInt(matIndexStr, 10);

            const mesh = this.meshList.find((m) => m.uuid === uuid);
            if (!mesh) return;

            const mat = Array.isArray(mesh.material)
                ? mesh.material[matIndex] || null
                : matIndex === 0
                  ? mesh.material
                  : null;

            if (mat) {
                mat[mapType] = originalTex;
                mat.needsUpdate = true;
            }
        });

        this.originalTextures.clear();
        this._updatePreview();
        this._showStatus("All textures reset", "success");
    }

    _showStatus(message, type) {
        const statusEl = document.getElementById("texture-updater-status");
        if (!statusEl) return;

        statusEl.textContent = message;
        statusEl.className = `texture-updater-status texture-updater-status-${type}`;

        setTimeout(() => {
            statusEl.textContent = "";
            statusEl.className = "texture-updater-status";
        }, 3000);
    }

    destroy() {
        this._resetAllTextures();
        document.removeEventListener(
            "modelLoadingComplete",
            this._boundOnModelLoad,
        );
        this._hidePanel();
        this._hideToggleButton();
        this._initialized = false;
        console.log("[TextureUpdater] Destroyed");
    }
}
